import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { logger } from '@adornis/base/logging.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { changeUserPassword, generatePassword } from '@adornis/users/server/password.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { buildNSCEmail } from '@nsc/smartteams-shared/email-helpers.js';
import { html } from 'lit';
import { config } from '../config.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { getFieldEngineerFilter } from '../features/permissions/api/getFieldEngineerFilter.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

/**
 * The onboarding email body, shared by the single re-send mutation and the bulk re-send below.
 * `tempPassword` is the plaintext temporary credential to show; omit it when the password isn't
 * being (re)set here.
 */
async function buildOnboardingMessage(feEmail: string, tempPassword?: string) {
  return Message.compose({
    subject: 'NSC Global: Welcome to SmartTeams',
    html: buildNSCEmail(html`
      <div>
        <p>Welcome onboard the NSC SmartTeams app!</p>
        <p>
          Your SmartTeams account has been created. Please use the credentials below to log in and complete the
          required training module.
        </p>
        <p><strong>Username:</strong> ${feEmail}</p>
        <p><strong>Temporary Password:</strong> ${tempPassword ?? 'Provided by your admin'}</p>
        <p>You&rsquo;ll be prompted to change your password at first login.</p>
        <p>
          <a
            href="${config.get('APP_URL')}"
            style="display: inline-block; background-color: #f37021; color: white; margin-top: 12px; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;"
          >
            Open SmartTeams App
          </a>
        </p>
        <p>Or click on this link: <a href="${config.get('APP_URL')}" target="_blank">${config.get('APP_URL')}</a></p>
      </div>
    `),
  });
}

function normalizePassword(password?: string): string | undefined {
  const value = password?.trim();
  return value ? value : undefined;
}

export const sendFieldEngineerOnboardingEmail = registerMutation({
  operationName: 'sendFieldEngineerOnboardingEmail',
  type: () => String,
  params: [
    { name: 'fieldEngineerId', type: () => String },
    { name: 'password', type: () => String },
  ],
  resolve: (fieldEngineerId: string, password?: string) => async () => {
    const hasPermission = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEImportExportAdmin));
    if (!hasPermission) {
      throw new ExpectedError('No permission to notify field engineers');
    }

    const fieldEngineer = await getByID(
      SmartTeamsFieldEngineer,
      fieldEngineerId,
    )({ _id: 1, name: 1, email: 1, userId: 1 });

    if (!fieldEngineer) {
      throw new ExpectedError('Field engineer not found');
    }

    if (!fieldEngineer.email) {
      throw new ExpectedError('Field engineer has no email address');
    }

    const givenPassword = normalizePassword(password);

    if (givenPassword) {
      if (!fieldEngineer.userId) {
        throw new ExpectedError(
          'Field engineer has no user account — use the "create user" action first; password was NOT sent.',
        );
      }

      const linkedUser = await (await getRawCollection<AdornisUser>(AdornisUser._collectionName)).findOne(
        { _id: fieldEngineer.userId },
        { projection: { _id: 1, email: 1 } },
      );

      if (!linkedUser) {
        throw new ExpectedError(
          'Field engineer is linked to a user account that no longer exists — use the "create user" action to recreate it; password was NOT sent.',
        );
      }

      if (linkedUser.email?.toLocaleLowerCase() !== fieldEngineer.email.toLocaleLowerCase()) {
        throw new ExpectedError(
          `Linked user account email (${linkedUser.email}) differs from field engineer email (${fieldEngineer.email}) — fix the linkage first; password was NOT sent.`,
        );
      }

      // Onboarding intentionally does NOT enforce the password policy. This mutation is the shared
      // onboarding path for the CSV import and the "re-send onboarding email" action, where the
      // password is a temporary credential (the FE must change it on first login) and may come from
      // an import with simple values. Policy enforcement lives in the backoffice user-creation flows
      // (createFieldEngineerWithUser / createUserForFieldEngineer).
      await changeUserPassword(fieldEngineer.userId, givenPassword, AdornisUser);
      const collection = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
        SmartTeamsFieldEngineer._collectionName,
      );
      await collection.updateOne({ _id: fieldEngineerId }, { $set: { mustChangePassword: true } });
    }

    const message = await buildOnboardingMessage(fieldEngineer.email, givenPassword);

    await AdornisMailer.getInstance().sendMail(message, new Recipient(fieldEngineer.email));

    // Record the successful delivery. Absent/null means the onboarding email was never sent for
    // this field engineer; the value is refreshed on every successful send (re-send, CSV import,
    // create-with-user), so it always reflects the latest delivery. Runs only after sendMail
    // resolves — a send failure throws above and leaves the timestamp untouched.
    const feCollection = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
      SmartTeamsFieldEngineer._collectionName,
    );
    await feCollection.updateOne({ _id: fieldEngineerId }, { $set: { onboardingEmailSentAt: new Date() } });

    return `sent onboarding email to ${fieldEngineer.email}`;
  },
});

// ============================================================================
// Bulk re-send onboarding email to field engineers who still need to change their password
// (the "yellow marker" set: a login exists AND mustChangePassword === true), scoped to the caller
// and narrowed by the list's current filter. A fresh random password is generated for each.
// ============================================================================

/**
 * Mongo filter for the "yellow marker" field engineers within the current list scope + filter:
 * have a login (`userId`), can be emailed (`email`), and still owe a password change. The count
 * query and the bulk mutation share this so the number shown in the confirm dialog is exactly the
 * set that gets processed.
 *
 * Also enforces a once-per-day throttle: anyone whose `onboardingEmailSentAt` falls on the current
 * (UTC) day is excluded, so re-running the bulk send doesn't spam the same engineers. Engineers who
 * were never emailed (`onboardingEmailSentAt` null/absent) are always eligible.
 */
async function buildPendingOnboardingFilter(filter?: AdornisFilter): Promise<Record<string, unknown>> {
  const fieldEngineerFilter = await getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(
    f => f.parseMongoFilter(),
  );
  const startOfToday = new Date();
  startOfToday.setUTCHours(0, 0, 0, 0);
  return {
    $and: [
      { deletedAt: null },
      { mustChangePassword: true },
      { userId: { $type: 'string' } },
      { email: { $type: 'string' } },
      // Not already emailed today (null/absent = never emailed = eligible).
      { $or: [{ onboardingEmailSentAt: null }, { onboardingEmailSentAt: { $lt: startOfToday } }] },
      fieldEngineerFilter,
      filter?.getMongoFilter() ?? {},
    ],
  };
}

async function assertMayNotifyFieldEngineers(): Promise<void> {
  const hasPermission = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEImportExportAdmin));
  if (!hasPermission) throw new ExpectedError('No permission to notify field engineers');
}

/** How many field engineers a bulk re-send would target for the given list filter. */
export const countFieldEngineersPendingOnboarding = registerQuery({
  operationName: 'countFieldEngineersPendingOnboarding',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<number> => {
    await assertMayNotifyFieldEngineers();
    const mongoFilter = await buildPendingOnboardingFilter(filter);
    const coll = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(SmartTeamsFieldEngineer._collectionName);
    return coll.countDocuments(mongoFilter);
  },
});

/**
 * A single field engineer the bulk re-send could not process, with the reason — carries the id so
 * the UI can link straight to the engineer to fix it (e.g. an invalid email from the import).
 *
 * @entity OnboardingResendFailure
 */
@Entity()
export class OnboardingResendFailure extends AdornisEntity {
  static override _class = 'OnboardingResendFailure';

  static override resolveID(entity: OnboardingResendFailure): string {
    return entity._id;
  }

  /** Field engineer `_id`. */
  @Field(() => ID) _id!: string;
  @Field(() => String) name: Maybe<string>;
  @Field(() => String) email: Maybe<string>;
  @Field(() => String) error: Maybe<string>;

  constructor(doc: PartialEntityData<OnboardingResendFailure>) {
    super(doc);
  }
}

/**
 * Outcome of a bulk onboarding re-send: how many field engineers matched, how many emails went out,
 * and the ones that failed (each failure is isolated so one bad address never blocks the rest).
 *
 * @entity OnboardingResendResult
 */
@Entity()
export class OnboardingResendResult extends AdornisEntity {
  static override _class = 'OnboardingResendResult';

  static override resolveID(entity: OnboardingResendResult): string {
    return entity._id;
  }

  @Field(() => ID) _id!: string;
  @Field(() => Int) targeted: Maybe<number>;
  @Field(() => Int) sent: Maybe<number>;
  @Field(() => Int) failed: Maybe<number>;
  @Field(() => [OnboardingResendFailure]) failures: Maybe<OnboardingResendFailure[]>;

  constructor(doc: PartialEntityData<OnboardingResendResult>) {
    super(doc);
  }
}

/**
 * Generates a new random password for, and re-sends the onboarding email to, every "yellow marker"
 * field engineer matching the current list filter. Targets only field engineers with
 * `mustChangePassword === true`, so it is safe to run from a view that also shows already-onboarded
 * engineers — those are skipped. Each engineer is processed independently; a single failure is
 * counted and logged, not fatal.
 */
export const resendOnboardingForFilteredFieldEngineers = registerMutation({
  operationName: 'resendOnboardingForFilteredFieldEngineers',
  type: () => OnboardingResendResult,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<OnboardingResendResult> => {
    await assertMayNotifyFieldEngineers();
    const mongoFilter = await buildPendingOnboardingFilter(filter);
    const coll = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(SmartTeamsFieldEngineer._collectionName);
    const fieldEngineers = await coll
      .find(mongoFilter, { projection: { _id: 1, name: 1, email: 1, userId: 1 } })
      .toArray();

    let sent = 0;
    const failures: OnboardingResendFailure[] = [];
    for (const fe of fieldEngineers) {
      try {
        if (!fe.userId || !fe.email) {
          failures.push(
            new OnboardingResendFailure({
              _id: fe._id,
              name: fe.name ?? undefined,
              email: fe.email ?? undefined,
              error: 'Missing login or email address',
            }),
          );
          continue;
        }
        const password = generatePassword();
        // See the single re-send above: onboarding intentionally does not enforce the password
        // policy — this is a temporary credential the FE must change on first login.
        await changeUserPassword(fe.userId, password, AdornisUser);
        const message = await buildOnboardingMessage(fe.email, password);
        await AdornisMailer.getInstance().sendMail(message, new Recipient(fe.email));
        await coll.updateOne(
          { _id: fe._id },
          { $set: { mustChangePassword: true, onboardingEmailSentAt: new Date() } },
        );
        sent++;
      } catch (error) {
        failures.push(
          new OnboardingResendFailure({
            _id: fe._id,
            name: fe.name ?? undefined,
            email: fe.email ?? undefined,
            error: error instanceof Error ? error.message : String(error),
          }),
        );
        logger.error({ error, fieldEngineerId: fe._id }, 'Failed to re-send onboarding email in bulk run');
      }
    }

    return new OnboardingResendResult({
      _id: 'onboarding-resend-result',
      targeted: fieldEngineers.length,
      sent,
      failed: failures.length,
      failures,
    });
  },
});
